import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DateRangeIcon from "@mui/icons-material/DateRange";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions, ApexAnnotations } from "apexcharts";

import { getAllMeasurements } from "../../services/physicalMeasurementsApi";
import { calculateBodyMetrics, calculateAgeFromDOB, calculateAgeInYears } from "../../utils/bodyMetrics";
import {
  getWhoChartSeries,
  WhoIndicator,
  normalizeGender,
  Gender,
  WhoChartPoint
} from "../../constants/whoData";

interface MetricConfig {
  key: string;
  label: string;
  unit: string;
  hasWhoRef: boolean;
  whoIndicator?: WhoIndicator;
  yMinDefault: number;
  yMaxDefault: number;
}

const METRIC_OPTIONS: MetricConfig[] = [
  { key: "height", label: "Height (cm)", unit: "cm", hasWhoRef: true, whoIndicator: "height", yMinDefault: 90, yMaxDefault: 200 },
  { key: "weight", label: "Weight (kg)", unit: "kg", hasWhoRef: true, whoIndicator: "weight", yMinDefault: 10, yMaxDefault: 100 },
  { key: "bmi", label: "BMI", unit: "kg/m²", hasWhoRef: true, whoIndicator: "bmi", yMinDefault: 10, yMaxDefault: 35 },
  { key: "heartRate", label: "Heart Rate (bpm)", unit: "bpm", hasWhoRef: false, yMinDefault: 50, yMaxDefault: 130 },
  { key: "bodyFat", label: "Body Fat (%)", unit: "%", hasWhoRef: false, yMinDefault: 3, yMaxDefault: 40 },
  { key: "muscleMass", label: "Muscle Mass (kg)", unit: "kg", hasWhoRef: false, yMinDefault: 5, yMaxDefault: 50 },
  { key: "boneMass", label: "Bone Mass (kg)", unit: "kg", hasWhoRef: false, yMinDefault: 1, yMaxDefault: 5 },
  { key: "bmr", label: "Pediatric BMR Estimate (kcal)", unit: "kcal", hasWhoRef: false, yMinDefault: 600, yMaxDefault: 2200 },
  { key: "protein", label: "Protein (%)", unit: "%", hasWhoRef: false, yMinDefault: 5, yMaxDefault: 25 },
  { key: "bodyMoisture", label: "Body Moisture (%)", unit: "%", hasWhoRef: false, yMinDefault: 30, yMaxDefault: 75 },
  { key: "visceralFat", label: "Visceral Index (Estimated)", unit: "rating", hasWhoRef: false, yMinDefault: 1, yMaxDefault: 15 },
  { key: "subcutaneousFat", label: "Subcutaneous Fat (%)", unit: "%", hasWhoRef: false, yMinDefault: 3, yMaxDefault: 35 },
  { key: "leanBodyMass", label: "Lean Body Mass (kg)", unit: "kg", hasWhoRef: false, yMinDefault: 10, yMaxDefault: 80 },
  { key: "standardWeight", label: "Reference Median Weight (P50) (kg)", unit: "kg", hasWhoRef: false, yMinDefault: 15, yMaxDefault: 90 }
];

const AGE_FILTER_OPTIONS = [
  { value: "all", label: "All Ages (5 – 19 Years)" },
  { value: "5_9", label: "5 – 9 Years (Early Childhood / Primary)" },
  { value: "10_14", label: "10 – 14 Years (Middle Childhood / Adolescence)" },
  { value: "15_19", label: "15 – 19 Years (Late Adolescence / Youth)" }
];

const GENDER_FILTER_OPTIONS = [
  { value: "all", label: "All Genders (Boys & Girls)" },
  { value: "boys", label: "Boys (Male)" },
  { value: "girls", label: "Girls (Female)" }
];

const CumulativeTrendPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<any[]>([]);

  // Filter States
  const [selectedMetricKey, setSelectedMetricKey] = useState<string>("height");
  const [selectedGender, setSelectedGender] = useState<string>("all");
  const [selectedAgeFilter, setSelectedAgeFilter] = useState<string>("all");

  const fetchPopulationData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllMeasurements(0, 2000);
      const items = response.data?.content || [];
      setMeasurements(items);
    } catch (err: any) {
      console.error("Failed to load cumulative population data:", err);
      setError("Unable to load measurement data from the server. Please ensure the backend is active.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopulationData();
  }, []);

  const activeMetric = METRIC_OPTIONS.find(m => m.key === selectedMetricKey) || METRIC_OPTIONS[0];

  // 1. Process and enrich records with exact calculations
  const enrichedRecords = useMemo(() => {
    return measurements.map(item => {
      const student = item.student || {};
      const measurementDate = item.lastMeasurement || item.measurementDate || item.createdAt || new Date().toISOString();
      const dob = student.dob || "";
      const rawGender = student.gender || item.gender || "Male";
      const normalizedGender: Gender = normalizeGender(rawGender) || "Male";
      const height = Number(item.height) || 0;
      const weight = Number(item.weight) || 0;
      const heartRate = Number(item.heartRate) || 70;

      const intAge = calculateAgeFromDOB(dob, measurementDate);
      const exactAge = calculateAgeInYears(dob, measurementDate);
      const metrics = calculateBodyMetrics(height, weight, dob, normalizedGender, heartRate, measurementDate);

      // Safe continuous age value
      const ageAtMeasurement = exactAge > 0 ? exactAge : (intAge > 0 ? intAge : (Number(item.age || student.age) || 5));

      return {
        id: item.id,
        studentId: student.id || item.studentId,
        studentName: student.name || item.name || "Student",
        gender: normalizedGender,
        dob,
        measurementDate,
        intAge,
        exactAge: ageAtMeasurement,
        height,
        weight,
        heartRate,
        bmi: typeof item.bmi === "number" && item.bmi > 0 ? item.bmi : typeof metrics.bmi === "number" ? metrics.bmi : 0,
        bodyFat: typeof item.bodyFat === "number" && item.bodyFat > 0 ? item.bodyFat : typeof metrics.bodyFat === "number" ? metrics.bodyFat : 0,
        muscleMass: typeof item.muscleMass === "number" && item.muscleMass > 0 ? item.muscleMass : typeof metrics.muscleMass === "number" ? metrics.muscleMass : 0,
        boneMass: typeof item.boneMass === "number" && item.boneMass > 0 ? item.boneMass : typeof metrics.boneMass === "number" ? metrics.boneMass : 0,
        bmr: typeof item.bmr === "number" && item.bmr > 0 ? item.bmr : typeof metrics.bmr === "number" ? metrics.bmr : 0,
        protein: typeof item.protein === "number" && item.protein > 0 ? item.protein : typeof metrics.protein === "number" ? metrics.protein : 0,
        bodyMoisture: typeof item.bodyMoisture === "number" && item.bodyMoisture > 0 ? item.bodyMoisture : typeof metrics.bodyMoisture === "number" ? metrics.bodyMoisture : 0,
        visceralFat: typeof item.visceralFat === "number" && item.visceralFat > 0 ? item.visceralFat : typeof metrics.visceralFat === "number" ? metrics.visceralFat : 0,
        subcutaneousFat: typeof item.subcutaneousFat === "number" && item.subcutaneousFat > 0 ? item.subcutaneousFat : typeof metrics.subcutaneousFat === "number" ? metrics.subcutaneousFat : 0,
        leanBodyMass: typeof item.leanBodyMass === "number" && item.leanBodyMass > 0 ? item.leanBodyMass : typeof metrics.leanBodyMass === "number" ? metrics.leanBodyMass : 0,
        standardWeight: typeof item.standardWeight === "number" && item.standardWeight > 0 ? item.standardWeight : typeof metrics.standardWeight === "number" ? metrics.standardWeight : 0
      };
    });
  }, [measurements]);

  // 2. Apply Age & Gender Filtering
  const filteredCohort = useMemo(() => {
    return enrichedRecords.filter(item => {
      // Gender Filter
      if (selectedGender === "boys" && item.gender !== "Male") return false;
      if (selectedGender === "girls" && item.gender !== "Female") return false;

      // Age Filter (based on exact age at measurement)
      const a = item.exactAge;
      if (selectedAgeFilter === "5_9" && (a < 5 || a >= 10)) return false;
      if (selectedAgeFilter === "10_14" && (a < 10 || a >= 15)) return false;
      if (selectedAgeFilter === "15_19" && (a < 15 || a > 20)) return false;
      if (selectedAgeFilter === "all" && (a < 5 || a > 20)) return false;

      return true;
    });
  }, [enrichedRecords, selectedGender, selectedAgeFilter]);

  // 3. Extract valid metric data points
  const metricValues = useMemo(() => {
    return filteredCohort
      .map(item => ({
        ...item,
        value: Number((item as any)[activeMetric.key]) || 0
      }))
      .filter(item => item.value > 0);
  }, [filteredCohort, activeMetric]);

  // 4. Summary Statistics
  const stats = useMemo(() => {
    if (metricValues.length === 0) {
      return {
        totalStudents: 0,
        totalSamples: 0,
        boysCount: 0,
        girlsCount: 0,
        average: 0,
        min: 0,
        max: 0,
        latestDate: "-"
      };
    }

    const uniqueStudents = new Set(metricValues.map(m => m.studentId || m.studentName)).size;
    const boysCount = metricValues.filter(m => m.gender === "Male").length;
    const girlsCount = metricValues.filter(m => m.gender === "Female").length;
    const values = metricValues.map(m => m.value);
    const sum = values.reduce((acc, v) => acc + v, 0);
    const avg = sum / values.length;
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);

    const sortedDates = [...metricValues].sort((a, b) => new Date(b.measurementDate).getTime() - new Date(a.measurementDate).getTime());
    const latest = sortedDates[0]?.measurementDate ? new Date(sortedDates[0].measurementDate).toLocaleDateString("en-GB") : "-";

    return {
      totalStudents: uniqueStudents,
      totalSamples: metricValues.length,
      boysCount,
      girlsCount,
      average: +avg.toFixed(2),
      min: +minVal.toFixed(2),
      max: +maxVal.toFixed(2),
      latestDate: latest
    };
  }, [metricValues]);

  // 5. Build WHO curves and ApexCharts Configuration
  const chartSeriesAndOptions = useMemo(() => {
    const isWhoIndicator = activeMetric.hasWhoRef && !!activeMetric.whoIndicator;
    const whoInd: WhoIndicator = activeMetric.whoIndicator || "height";

    // Age bounds based on selected filter
    let minAge = 5;
    let maxAge = 19;
    if (selectedAgeFilter === "5_9") {
      minAge = 5;
      maxAge = 10;
    } else if (selectedAgeFilter === "10_14") {
      minAge = 10;
      maxAge = 15;
    } else if (selectedAgeFilter === "15_19") {
      minAge = 15;
      maxAge = 19;
    }

    // Fetch official reference series (WHO for Height/BMI, CDC for Weight)
    const boysWhoSeries: WhoChartPoint[] = isWhoIndicator
      ? getWhoChartSeries(whoInd, "Male", minAge, maxAge, 0.5)
      : [];

    const girlsWhoSeries: WhoChartPoint[] = isWhoIndicator
      ? getWhoChartSeries(whoInd, "Female", minAge, maxAge, 0.5)
      : [];

    // Separate student scatter points by gender
    const boysScatter = metricValues
      .filter(m => m.gender === "Male")
      .map(item => ({
        x: +item.exactAge.toFixed(2),
        y: +item.value.toFixed(2),
        name: item.studentName,
        date: item.measurementDate,
        gender: "Male"
      }));

    const girlsScatter = metricValues
      .filter(m => m.gender === "Female")
      .map(item => ({
        x: +item.exactAge.toFixed(2),
        y: +item.value.toFixed(2),
        name: item.studentName,
        date: item.measurementDate,
        gender: "Female"
      }));

    // Cohort Mean line by integer age brackets
    const ageGroups: Record<number, number[]> = {};
    metricValues.forEach(m => {
      const a = Math.round(m.exactAge);
      if (!ageGroups[a]) ageGroups[a] = [];
      ageGroups[a].push(m.value);
    });

    const cohortAverageData = Object.keys(ageGroups)
      .map(Number)
      .sort((a, b) => a - b)
      .map(age => ({
        x: age,
        y: +(ageGroups[age].reduce((acc, v) => acc + v, 0) / ageGroups[age].length).toFixed(1)
      }));

    // Construct Chart Series
    const series: any[] = [];

    if (isWhoIndicator) {
      if (selectedGender === "boys") {
        // Full Boys Percentile Curves
        series.push(
          { name: "3rd percentile", data: boysWhoSeries.map(d => ({ x: d.age, y: +d.p3.toFixed(2) })), color: "#64748B", type: "line" },
          { name: "15th percentile", data: boysWhoSeries.map(d => ({ x: d.age, y: +d.p15.toFixed(2) })), color: "#EF4444", type: "line" },
          { name: "50th percentile (Median)", data: boysWhoSeries.map(d => ({ x: d.age, y: +d.p50.toFixed(2) })), color: "#10B981", type: "line" },
          { name: "85th percentile", data: boysWhoSeries.map(d => ({ x: d.age, y: +d.p85.toFixed(2) })), color: "#F59E0B", type: "line" },
          { name: "97th percentile", data: boysWhoSeries.map(d => ({ x: d.age, y: +d.p97.toFixed(2) })), color: "#8B5CF6", type: "line" }
        );
      } else if (selectedGender === "girls") {
        // Full Girls Percentile Curves
        series.push(
          { name: "3rd percentile", data: girlsWhoSeries.map(d => ({ x: d.age, y: +d.p3.toFixed(2) })), color: "#64748B", type: "line" },
          { name: "15th percentile", data: girlsWhoSeries.map(d => ({ x: d.age, y: +d.p15.toFixed(2) })), color: "#EF4444", type: "line" },
          { name: "50th percentile (Median)", data: girlsWhoSeries.map(d => ({ x: d.age, y: +d.p50.toFixed(2) })), color: "#10B981", type: "line" },
          { name: "85th percentile", data: girlsWhoSeries.map(d => ({ x: d.age, y: +d.p85.toFixed(2) })), color: "#F59E0B", type: "line" },
          { name: "97th percentile", data: girlsWhoSeries.map(d => ({ x: d.age, y: +d.p97.toFixed(2) })), color: "#8E24AA", type: "line" }
        );
      } else {
        // Gender = "all": Display both Boys and Girls median reference curves (distinct, never merged!)
        const boysMedianLabel = whoInd === "weight" ? "CDC Boys Median (P50)" : "WHO Boys Median (P50)";
        const girlsMedianLabel = whoInd === "weight" ? "CDC Girls Median (P50)" : "WHO Girls Median (P50)";
        series.push(
          { name: boysMedianLabel, data: boysWhoSeries.map(d => ({ x: d.age, y: +d.p50.toFixed(2) })), color: "#2563EB", type: "line" },
          { name: girlsMedianLabel, data: girlsWhoSeries.map(d => ({ x: d.age, y: +d.p50.toFixed(2) })), color: "#EC4899", type: "line" }
        );
      }
    }

    // Cohort Average Line
    if (cohortAverageData.length > 1) {
      series.push({
        name: "Cohort Average",
        data: cohortAverageData,
        color: "#1E293B",
        type: "line"
      });
    }

    // Population Scatter Points (Boys & Girls separated for color and clarity)
    if (selectedGender === "all" || selectedGender === "boys") {
      if (boysScatter.length > 0) {
        series.push({
          name: selectedGender === "all" ? "Boys (Actual Measurements)" : "Student Measurements",
          data: boysScatter.map(p => ({ x: p.x, y: p.y })),
          color: "#2563EB",
          type: "scatter"
        });
      }
    }

    if (selectedGender === "all" || selectedGender === "girls") {
      if (girlsScatter.length > 0) {
        series.push({
          name: selectedGender === "all" ? "Girls (Actual Measurements)" : "Student Measurements",
          data: girlsScatter.map(p => ({ x: p.x, y: p.y })),
          color: "#EC4899",
          type: "scatter"
        });
      }
    }

    // Dynamic Axis Bounds
    const allYValues = metricValues.map(m => m.value);
    const yMinCalc = allYValues.length > 0 ? Math.min(...allYValues) : activeMetric.yMinDefault;
    const yMaxCalc = allYValues.length > 0 ? Math.max(...allYValues) : activeMetric.yMaxDefault;
    const yMin = Math.max(0, Math.floor(Math.min(activeMetric.yMinDefault, yMinCalc * 0.9)));
    const yMax = Math.ceil(Math.max(activeMetric.yMaxDefault, yMaxCalc * 1.1));

    const xMin = selectedAgeFilter === "5_9" ? 5 :
                 selectedAgeFilter === "10_14" ? 10 :
                 selectedAgeFilter === "15_19" ? 15 : 5;

    const xMax = selectedAgeFilter === "5_9" ? 10 :
                 selectedAgeFilter === "10_14" ? 15 :
                 selectedAgeFilter === "15_19" ? 19.5 : 19.5;

    // Normal Range Annotation for BMI
    const yAxisAnnotations: ApexAnnotations["yaxis"] = [];
    if (activeMetric.key === "bmi" && (selectedGender === "boys" || selectedGender === "girls")) {
      const whoRefPoints = selectedGender === "girls" ? girlsWhoSeries : boysWhoSeries;
      if (whoRefPoints.length > 0) {
        const minSd2Neg = Math.min(...whoRefPoints.map(p => p.sd2neg || p.p3));
        const maxSd1 = Math.max(...whoRefPoints.map(p => p.sd1 || p.p85));

        yAxisAnnotations.push({
          y: minSd2Neg,
          y2: maxSd1,
          fillColor: "#10B981",
          opacity: 0.12,
          borderColor: "#10B981",
          strokeDashArray: 4,
          label: {
            text: `WHO Normal Weight Band (${selectedGender === "girls" ? "Girls" : "Boys"})`,
            style: {
              fontSize: "11px",
              fontWeight: 600,
              color: "#065F46",
              background: "#D1FAE5"
            },
            position: "left"
          }
        });
      }
    }

    const options: ApexOptions = {
      chart: {
        type: "line",
        fontFamily: "Inter, Roboto, 'Segoe UI', sans-serif",
        animations: {
          enabled: false
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        },
        zoom: { enabled: true }
      },
      stroke: {
        width: series.map(s => {
          if (s.type === "scatter") return 0;
          if (s.name.includes("Median")) return 2.8;
          if (s.name.includes("Cohort Average")) return 3.5;
          return 1.8;
        }),
        dashArray: series.map(s => {
          if (s.name.includes("percentile") && !s.name.includes("Median")) return 3;
          if (s.name.includes("Boys Median") || s.name.includes("Girls Median")) return 4;
          return 0;
        }),
        curve: "smooth"
      },
      markers: {
        size: series.map(s => (s.type === "scatter" ? 7 : s.name.includes("Cohort Average") ? 5 : 0)),
        strokeColors: "#FFFFFF",
        strokeWidth: 2.5,
        hover: { size: 10 }
      },
      xaxis: {
        type: "numeric",
        min: xMin,
        max: xMax,
        tickAmount: Math.min(15, Math.ceil(xMax - xMin)),
        title: {
          text: "Student Age at Measurement (Years)",
          style: { fontWeight: 600, color: "#1E293B", fontSize: "12px" }
        },
        axisBorder: { show: true, color: "#E2E8F0" },
        axisTicks: { show: true, color: "#E2E8F0" },
        labels: {
          style: { colors: "#64748B", fontSize: "11px", fontWeight: 500 },
          formatter: val => `${Number(val).toFixed(1)} yrs`
        }
      },
      yaxis: {
        min: yMin,
        max: yMax,
        title: {
          text: `${activeMetric.label}`,
          style: { fontWeight: 600, color: "#1E293B", fontSize: "12px" }
        },
        axisBorder: { show: true, color: "#E2E8F0" },
        axisTicks: { show: true, color: "#E2E8F0" },
        labels: {
          style: { colors: "#64748B", fontSize: "11px", fontWeight: 500 },
          formatter: val => `${Number(val).toFixed(0)} ${activeMetric.unit}`
        }
      },
      grid: {
        borderColor: "#F1F5F9",
        strokeDashArray: 3,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } }
      },
      annotations: {
        yaxis: yAxisAnnotations
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        fontSize: "12px",
        fontFamily: "Inter, Roboto, sans-serif",
        fontWeight: 500,
        itemMargin: { horizontal: 8, vertical: 4 }
      },
      tooltip: {
        shared: false,
        intersect: false,
        custom: ({ series: sList, seriesIndex, dataPointIndex, w }) => {
          const sName = w.globals.seriesNames[seriesIndex];
          const val = sList[seriesIndex]?.[dataPointIndex];
          const ageVal = w.globals.seriesX[seriesIndex]?.[dataPointIndex];

          if (sName.includes("Measurements") || sName.includes("Boys (Actual") || sName.includes("Girls (Actual")) {
            const isGirl = sName.includes("Girls");
            const ptList = isGirl ? girlsScatter : (selectedGender === "all" ? boysScatter : (boysScatter.length > 0 ? boysScatter : girlsScatter));
            const pt = ptList[dataPointIndex];
            if (pt) {
              const formattedDate = pt.date && !isNaN(new Date(pt.date).getTime())
                ? new Date(pt.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                : "-";
              const tagColor = pt.gender === "Female" ? "#EC4899" : "#2563EB";
              const tagBg = pt.gender === "Female" ? "#FDF2F8" : "#EFF6FF";
              return `
                <div style="padding: 12px 16px; font-family: Inter, Roboto, sans-serif; font-size: 12px; line-height: 1.6; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(8px); border: 1px solid ${tagColor}; border-radius: 10px; box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.08);">
                  <div style="font-weight: 700; color: #0F172A; margin-bottom: 6px; border-bottom: 1px solid #F1F5F9; padding-bottom: 4px; display: flex; align-items: center; justify-content: space-between; gap: 8px;">
                    <span>${pt.name}</span>
                    <span style="font-size: 10px; font-weight: 600; padding: 2px 6px; background: ${tagBg}; color: ${tagColor}; border-radius: 4px;">${pt.gender}</span>
                  </div>
                  <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Date:</span> <b style="color: #1E293B;">${formattedDate}</b></div>
                  <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Age:</span> <b style="color: #1E293B;">${typeof pt.x === "number" ? pt.x.toFixed(2) : pt.x} yrs</b></div>
                  <div style="margin-top: 6px; padding-top: 4px; border-top: 1px dashed #E2E8F0; display: flex; justify-content: space-between; align-items: center; gap: 12px;">
                    <span style="color: ${tagColor}; font-weight: 600;">${activeMetric.label}:</span>
                    <b style="font-size: 14px; color: ${tagColor};">${val} ${activeMetric.unit}</b>
                  </div>
                </div>
              `;
            }
          }

          if (sName.includes("Cohort Average")) {
            return `
              <div style="padding: 10px 14px; font-family: Inter, Roboto, sans-serif; font-size: 12px; line-height: 1.5; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(8px); border: 1px solid #1E293B; border-left: 4px solid #1E293B; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);">
                <div style="font-weight: 700; color: #0F172A;">Cohort Mean (Age ~${typeof ageVal === "number" ? ageVal.toFixed(0) : ageVal}y)</div>
                <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px; margin-top: 2px;"><span>Average:</span> <b style="color: #0F172A;">${val} ${activeMetric.unit}</b></div>
              </div>
            `;
          }

          const refLabel = activeMetric.key === "weight" ? "CDC Reference" : "WHO Reference";
          const color = w.globals.colors[seriesIndex] || "#64748B";
          return `
            <div style="padding: 10px 14px; font-family: Inter, Roboto, sans-serif; font-size: 12px; line-height: 1.5; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(8px); border: 1px solid #E2E8F0; border-left: 3px solid ${color}; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);">
              <div style="font-weight: 600; color: #1E293B; margin-bottom: 2px;">${sName}</div>
              <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Age:</span> <b style="color: #1E293B;">${typeof ageVal === "number" ? ageVal.toFixed(1) : ageVal} yrs</b></div>
              <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>${refLabel}:</span> <b style="color: #1E293B;">${val} ${activeMetric.unit}</b></div>
            </div>
          `;
        }
      }
    };

    return { series, options };
  }, [metricValues, activeMetric, selectedGender, selectedAgeFilter]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#F8FAFC", minHeight: "calc(100vh - 70px)" }}>
      {/* Header Bar */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: "1px solid #E2E8F0", bgcolor: "#FFFFFF", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: "#EFF6FF", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TrendingUpIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A", letterSpacing: "-0.02em" }}>
                  Cumulative Population Trends
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Population-level health analytics, scatter distributions, and standardized growth reference curves
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPopulationData}
            size="small"
            sx={{
              borderColor: "#CBD5E1",
              color: "#334155",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": { borderColor: "#94A3B8", bgcolor: "#F8FAFC" }
            }}
          >
            Refresh Data
          </Button>
        </Stack>

        <Divider sx={{ my: 2, borderColor: "#F1F5F9" }} />

        {/* Filters Toolbar */}
        <Grid container spacing={2} alignItems="center">
          {/* Metric Selector */}
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="metric-select-label" sx={{ fontWeight: 600, color: "#334155" }}>Select Metric</InputLabel>
              <Select
                labelId="metric-select-label"
                value={selectedMetricKey}
                label="Select Metric"
                onChange={e => setSelectedMetricKey(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {METRIC_OPTIONS.map(m => (
                  <MenuItem key={m.key} value={m.key}>
                    {m.label} {m.key === "weight" ? "★ (CDC 5–19y)" : m.hasWhoRef ? "★ (WHO 5–19y)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Gender Filter */}
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="gender-select-label" sx={{ fontWeight: 600, color: "#334155" }}>Gender Category</InputLabel>
              <Select
                labelId="gender-select-label"
                value={selectedGender}
                label="Gender Category"
                onChange={e => setSelectedGender(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {GENDER_FILTER_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Age Filter */}
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="age-select-label" sx={{ fontWeight: 600, color: "#334155" }}>Age Category</InputLabel>
              <Select
                labelId="age-select-label"
                value={selectedAgeFilter}
                label="Age Category"
                onChange={e => setSelectedAgeFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {AGE_FILTER_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 3, bgcolor: "#FFFFFF", p: 0.5, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ p: 0.7, borderRadius: 1.5, bgcolor: "#EFF6FF", color: "#2563EB", display: "flex" }}>
                  <PeopleAltIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing="0.04em">COHORT SIZE</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, color: "#0F172A" }}>
                {stats.totalStudents} <Typography component="span" variant="caption" color="text.secondary">Students</Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2.4}>
          <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 3, bgcolor: "#FFFFFF", p: 0.5, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ p: 0.7, borderRadius: 1.5, bgcolor: "#F5F3FF", color: "#8B5CF6", display: "flex" }}>
                  <AssessmentIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing="0.04em">MEASUREMENTS</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, color: "#0F172A" }}>
                {stats.totalSamples} <Typography component="span" variant="caption" color="text.secondary">({stats.boysCount}M / {stats.girlsCount}F)</Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2.4}>
          <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 3, bgcolor: "#FFFFFF", p: 0.5, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ p: 0.7, borderRadius: 1.5, bgcolor: "#ECFDF5", color: "#10B981", display: "flex" }}>
                  <TrendingUpIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing="0.04em">COHORT MEAN</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, color: "#10B981" }}>
                {stats.average} <Typography component="span" variant="caption" color="text.secondary">{activeMetric.unit}</Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={2.4}>
          <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 3, bgcolor: "#FFFFFF", p: 0.5, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ p: 0.7, borderRadius: 1.5, bgcolor: "#FFFBEB", color: "#F59E0B", display: "flex" }}>
                  <AssessmentIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing="0.04em">COHORT RANGE</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, color: "#F59E0B" }}>
                {stats.min} – {stats.max} <Typography component="span" variant="caption" color="text.secondary">{activeMetric.unit}</Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 3, bgcolor: "#FFFFFF", p: 0.5, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ p: 0.7, borderRadius: 1.5, bgcolor: "#EEF2FF", color: "#6366F1", display: "flex" }}>
                  <DateRangeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing="0.04em">LATEST RECORD</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1, color: "#6366F1" }}>
                {stats.latestDate}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Chart Paper */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: "1px solid #E2E8F0", bgcolor: "#FFFFFF", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        {loading ? (
          <Box sx={{ py: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={40} sx={{ color: "#2563EB", mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading population measurement records...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 4, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : metricValues.length === 0 ? (
          <Box sx={{ py: 10, textAlign: "center", bgcolor: "#F8FAFC", borderRadius: 3, border: "1px dashed #CBD5E1" }}>
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              No measurement data available for the selected filters.
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              Try adjusting the Age Category or Gender Category filter above.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em" }}>
                  {activeMetric.label} — Population Distribution
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {activeMetric.key === "weight"
                    ? "Plotted alongside official CDC Weight-for-Age Reference (2–20 years) (P3, P15, P50, P85, P97)."
                    : activeMetric.hasWhoRef
                    ? "Plotted alongside official WHO Child Growth Reference 2007 (P3, P15, P50, P85, P97)."
                    : "Plotted with empirical cohort mean and individual student distribution."}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: { xs: 1, sm: 0 } }}>
                <Chip size="small" label={`Metric: ${activeMetric.label}`} sx={{ bgcolor: "#0F172A", color: "#FFFFFF", fontWeight: 600, borderRadius: 1.5 }} />
                <Chip size="small" label={`Gender: ${selectedGender === "boys" ? "Boys (Male)" : selectedGender === "girls" ? "Girls (Female)" : "All Genders"}`} variant="outlined" sx={{ borderColor: "#CBD5E1", color: "#334155", fontWeight: 500, borderRadius: 1.5 }} />
                <Chip size="small" label={`Age: ${AGE_FILTER_OPTIONS.find(a => a.value === selectedAgeFilter)?.label || "All Ages"}`} variant="outlined" sx={{ borderColor: "#CBD5E1", color: "#334155", fontWeight: 500, borderRadius: 1.5 }} />
              </Stack>
            </Stack>

            <ReactApexChart
              key={`pop-chart-${activeMetric.key}-${selectedGender}-${selectedAgeFilter}`}
              options={chartSeriesAndOptions.options}
              series={chartSeriesAndOptions.series}
              type="line"
              height={500}
            />

            {/* Scientific Note for Reference Standards */}
            {activeMetric.key === "weight" ? (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: "#EFF6FF", borderRadius: 2, border: "1px solid #BFDBFE", display: "flex", alignItems: "center", gap: 1 }}>
                <InfoOutlinedIcon sx={{ color: "#2563EB", fontSize: 20 }} />
                <Typography variant="caption" sx={{ color: "#1E3A8A", fontWeight: 500 }}>
                  <strong>Weight Reference Standard:</strong> Continuous pediatric Weight-for-Age reference curves (5–19 years) are provided by the CDC Weight-for-Age Reference (2–20 years). Height and BMI reference curves are based on WHO 2007.
                </Typography>
              </Box>
            ) : activeMetric.hasWhoRef ? (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: "#F8FAFC", borderRadius: 2, border: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 1 }}>
                <InfoOutlinedIcon sx={{ color: "#64748B", fontSize: 20 }} />
                <Typography variant="caption" sx={{ color: "#475569", fontWeight: 500 }}>
                  <strong>Growth Reference Standard:</strong> Official WHO Child Growth Reference 2007 (5–19 years) published by the World Health Organization (P3, P15, P50 Median, P85, P97).
                </Typography>
              </Box>
            ) : null}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CumulativeTrendPage;
